import * as React from "react";

import { cn } from "../../lib/utils";
import { Button } from "./button";

export const LIST_PAGE_SIZE_OPTIONS = Object.freeze([10, 20, 50, 100]);

const ListTableShell = React.forwardRef(({ className, ...props }, ref) => (
  <section
    ref={ref}
    className={cn("min-w-0 overflow-hidden rounded-sm border border-zinc-200 bg-card dark:border-zinc-800", className)}
    {...props}
  />
));
ListTableShell.displayName = "ListTableShell";

const ListControls = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex min-w-0 flex-col gap-3 border-b border-zinc-200 dark:border-zinc-800", className)}
    {...props}
  />
));
ListControls.displayName = "ListControls";

const ListSelectorRow = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex min-w-0 flex-wrap items-end gap-2 border-b border-zinc-200 px-5 pt-4", className)}
    {...props}
  />
));
ListSelectorRow.displayName = "ListSelectorRow";

const ListTableViewport = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("max-w-full overflow-x-auto", className)}
    data-list-table-viewport="true"
    {...props}
  />
));
ListTableViewport.displayName = "ListTableViewport";

function geometryLength(value, label) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`ListTable geometry ${label} must be a positive pixel number.`);
  }
  return value;
}

function resolveTableGeometry(geometry) {
  if (!geometry?.length) return null;

  let fillCount = 0;
  let minimumWidth = 0;
  const columns = geometry.map((column, index) => {
    const kind = column?.kind;
    if (kind === "fill") {
      fillCount += 1;
      const minWidth = geometryLength(column.minWidth, `column ${index + 1} minWidth`);
      minimumWidth += minWidth;
      return { ...column, kind, minWidth };
    }
    if (kind === "fixed" || kind === "utility") {
      const width = geometryLength(column.width, `column ${index + 1} width`);
      minimumWidth += width;
      return { ...column, kind, width };
    }
    throw new Error(`ListTable geometry column ${index + 1} must use fixed, fill, or utility.`);
  });

  if (fillCount !== 1) {
    throw new Error("ListTable geometry currently requires exactly one fill column.");
  }
  return { columns, minimumWidth };
}

function resolveFrozenColumns(frozenColumns, resolvedGeometry) {
  if (frozenColumns == null) return null;
  if (!Number.isInteger(frozenColumns) || frozenColumns < 0) {
    throw new Error("ListTable frozenColumns must be a non-negative integer.");
  }
  if (!resolvedGeometry) {
    throw new Error("ListTable frozenColumns requires a geometry contract.");
  }
  if (frozenColumns > resolvedGeometry.columns.length) {
    throw new Error("ListTable frozenColumns must not exceed the geometry column count.");
  }

  let left = 0;
  const offsets = resolvedGeometry.columns.slice(0, frozenColumns).map((column, index) => {
    if (column.kind === "fill") {
      throw new Error(`ListTable frozen column ${index + 1} must have a deterministic width.`);
    }
    const offset = left;
    left += column.width;
    return offset;
  });
  return { count: frozenColumns, offsets };
}

const ListTable = React.forwardRef(({ className, geometry, frozenColumns, children, style, ...props }, ref) => {
  const resolvedGeometry = resolveTableGeometry(geometry);
  const resolvedFrozenColumns = resolveFrozenColumns(frozenColumns, resolvedGeometry);
  const tableStyle = resolvedGeometry
    ? { ...style, tableLayout: "fixed", minWidth: resolvedGeometry.minimumWidth }
    : style;
  const frozenTableStyle = resolvedFrozenColumns
    ? resolvedFrozenColumns.offsets.reduce(
      (nextStyle, offset, index) => ({ ...nextStyle, [`--list-table-frozen-column-${index + 1}-left`]: `${offset}px` }),
      tableStyle,
    )
    : tableStyle;
  return (
    <table
      ref={ref}
      className={cn("wms-table", className)}
      data-list-table-geometry={resolvedGeometry ? "fixed" : undefined}
      data-list-table-frozen-columns={resolvedFrozenColumns ? resolvedFrozenColumns.count : undefined}
      style={frozenTableStyle}
      {...props}
    >
      {resolvedGeometry ? (
        <colgroup>
          {resolvedGeometry.columns.map((column, index) => (
            <col
              key={column.key || index}
              data-column-kind={column.kind}
              data-column-key={column.key}
              style={column.kind === "fill"
                ? { minWidth: column.minWidth }
                : { width: column.width, minWidth: column.width, maxWidth: column.width }}
            />
          ))}
        </colgroup>
      ) : null}
      {children}
    </table>
  );
});
ListTable.displayName = "ListTable";

function ListTableState({
  colSpan,
  kind = "empty",
  children,
  onRetry,
  retryLabel = "Retry",
  className,
  testId,
}) {
  const isError = kind === "error";
  return (
    <tr data-list-state={kind} data-testid={testId}>
      <td
        colSpan={colSpan}
        className={cn("py-10 text-center text-zinc-500", isError && "text-destructive", className)}
      >
        <div className="flex flex-col items-center justify-center gap-3" role={isError ? "alert" : "status"}>
          <div>{children}</div>
          {isError && onRetry ? (
            <Button type="button" variant="retry" size="sm" onClick={onRetry}>
              {retryLabel}
            </Button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

function ListTableRefreshingRow({ active, colSpan, label = "Refreshing...", className, testId }) {
  if (!active) return null;
  return (
    <tr data-list-refreshing-row="true" data-testid={testId}>
      <td colSpan={colSpan} className={cn("text-center text-xs text-muted-foreground", className)}>
        <span role="status">{label}</span>
      </td>
    </tr>
  );
}

function ListErrorNotice({ children, onRetry, className, testId }) {
  return (
    <div
      className={cn("flex flex-col gap-3 border-b border-destructive/30 bg-destructive/5 px-5 py-4 text-sm text-destructive sm:flex-row sm:items-center sm:justify-between", className)}
      role="alert"
      data-testid={testId}
    >
      <span>{children}</span>
      {onRetry ? (
        <Button type="button" variant="retry" size="sm" onClick={onRetry}>Retry</Button>
      ) : null}
    </div>
  );
}

function ListPagination({
  page,
  pageSize,
  total,
  totalPages,
  loading = false,
  onPrevious,
  onNext,
  onPageSizeChange,
  pageSizeOptions = LIST_PAGE_SIZE_OPTIONS,
  pageSizeAriaLabel,
  className,
  testId,
  summaryTestId,
  pageTestId,
  previousTestId,
  nextTestId,
}) {
  const safeTotal = Math.max(0, Number(total) || 0);
  const safePage = Math.max(1, Number(page) || 1);
  const safePageSize = Math.max(1, Number(pageSize) || 10);
  const safeTotalPages = Math.max(0, Number(totalPages) || 0);
  const displayedTotalPages = safeTotal ? Math.max(1, safeTotalPages) : 0;
  const start = safeTotal ? (safePage - 1) * safePageSize + 1 : 0;
  const end = safeTotal ? Math.min(safePage * safePageSize, safeTotal) : 0;
  const canGoPrevious = safeTotal > 0 && safePage > 1;
  const canGoNext = safeTotal > 0 && safePage < displayedTotalPages;

  return (
    <footer
      className={cn("flex flex-wrap items-center justify-between gap-3 border-t border-zinc-200 px-5 py-4 text-xs text-zinc-600 dark:border-zinc-800 dark:text-zinc-400", className)}
      data-testid={testId}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span className="whitespace-nowrap tabular-nums" data-testid={summaryTestId}>
          <span className="text-muted-foreground">{`Showing ${start}\u2013${end} of `}</span>
          <span className="font-semibold text-foreground">{safeTotal}</span>
        </span>
        {onPageSizeChange ? (
          <select
            className="wms-filter-input !w-auto bg-card text-foreground"
            value={safePageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            aria-label={pageSizeAriaLabel || "Page size"}
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="neutral"
          size="sm"
          disabled={!canGoPrevious || loading}
          onClick={onPrevious}
          data-action-category="pagination"
          data-testid={previousTestId}
          aria-label="Previous page"
        >
          Previous
        </Button>
        {safeTotal > 0 ? (
          <span className="min-w-20 text-center font-medium tabular-nums" data-testid={pageTestId}>
            Page {safePage} of {displayedTotalPages}
          </span>
        ) : null}
        <Button
          type="button"
          variant="neutral"
          size="sm"
          disabled={!canGoNext || loading}
          onClick={onNext}
          data-action-category="pagination"
          data-testid={nextTestId}
          aria-label="Next page"
        >
          Next
        </Button>
      </div>
    </footer>
  );
}

export {
  ListControls,
  ListErrorNotice,
  ListPagination,
  ListSelectorRow,
  ListTable,
  ListTableRefreshingRow,
  ListTableShell,
  ListTableState,
  ListTableViewport,
};
